import { useEffect } from "react";
import Lenis from "lenis";

/**
 * The live Lenis instance, or null when smooth scrolling is off (reduced
 * motion). Exposed because `document.body.style.overflow = 'hidden'` does NOT
 * stop Lenis -- locking scroll for a modal requires lenis.stop().
 */
let instance: Lenis | null = null;

export function lockScroll(locked: boolean): void {
  if (instance) {
    if (locked) instance.stop();
    else instance.start();
  }
  // Also covers the reduced-motion case, where there is no Lenis instance.
  document.body.style.overflow = locked ? "hidden" : "";
}

/**
 * Jump to the top immediately. Both paths run on purpose: the route that owned
 * the Lenis instance may already have unmounted (destroying it and handing
 * scrolling back to the browser) by the time this is called.
 */
export function resetScroll(): void {
  instance?.scrollTo(0, { immediate: true });
  window.scrollTo(0, 0);
}

/**
 * Momentum scrolling, disabled when the viewer prefers reduced motion.
 *
 * Lenis takes over the scroll position, so native anchor jumps stop working.
 * We bridge them: in-page `#hash` clicks and hash navigations are handed to
 * lenis.scrollTo, keeping the existing nav links and `/#work` back-links intact.
 */
export function useSmoothScroll(): void {
  useEffect(() => {
    const prefersReduced = window.matchMedia(
      "(prefers-reduced-motion: reduce)"
    ).matches;
    if (prefersReduced) return;

    const lenis = new Lenis({ duration: 1.1, smoothWheel: true });
    instance = lenis;

    let frame = 0;
    const raf = (time: number) => {
      lenis.raf(time);
      frame = requestAnimationFrame(raf);
    };
    frame = requestAnimationFrame(raf);

    const scrollToHash = (hash: string) => {
      if (!hash || hash === "#") return;
      const target = document.querySelector(hash);
      if (target) lenis.scrollTo(target as HTMLElement, { offset: -80 });
    };

    const onClick = (event: MouseEvent) => {
      const anchor = (event.target as HTMLElement)?.closest?.("a");
      const href = anchor?.getAttribute("href");
      if (!href?.startsWith("#")) return;
      event.preventDefault();
      scrollToHash(href);
    };

    document.addEventListener("click", onClick);
    if (window.location.hash) {
      // Let the route paint before jumping.
      requestAnimationFrame(() => scrollToHash(window.location.hash));
    }

    return () => {
      document.removeEventListener("click", onClick);
      cancelAnimationFrame(frame);
      lenis.destroy();
      instance = null;
    };
  }, []);
}
